<template>
  <div>
    <!-- modo "tela normal": selects de mês e ano -->
    <div v-if="!printMode" class="mb-4">
      <div class="row g-3 align-items-end">
        <div class="col-auto">
          <label>Mês</label>
          <select v-model="mes" class="form-select">
            <option v-for="(lbl, i) in meses" :key="i" :value="i+1">{{ lbl }}</option>
          </select>
        </div>
        <div class="col-auto">
          <label>Ano</label>
          <input type="number" v-model.number="ano" class="form-control" min="2000" />
        </div>
        <div class="col-auto">
          <button class="btn btn-primary" @click="abrirPrint">Executar</button>
        </div>
      </div>
    </div>
    <!-- modo impressão: mostra duas tabelas separadas -->
    <div v-else>
      <h6 class="mb-3 text-center">{{ subtitle }}</h6>
      <!-- Tabela de RECEITAS -->
      <table class="table table-striped mb-4">
        <thead>
          <tr>
            <th>Subconta</th>
            <th class="text-right">{{ subtitle }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colspan="2" class="bg-secondary text-white text-center">RECEITAS</td>
          </tr>
          <template v-for="r in receitas" :key="r.plano.nome">
            <tr>
              <td colspan="2" class="fw-bold">{{ r.plano.nome }}</td>
            </tr>
            <tr v-for="item in r.itens" :key="item.subconta">
              <td class="ps-4">{{ item.subconta }}</td>
              <td class="text-right">{{ formatarMoeda(item.valor) }}</td>
            </tr>
          </template>
          <tr>
            <td><strong>Total Geral</strong></td>
            <td class="text-right"><strong>{{ formatarMoeda(totalReceitas) }}</strong></td>
          </tr>
        </tbody>
      </table>
      <!-- Tabela de DESPESAS -->
      <table class="table table-striped mb-4">
        <thead>
          <tr>
            <th>Subconta</th>
            <th class="text-right">{{ subtitle }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colspan="2" class="bg-secondary text-white text-center">DESPESAS</td>
          </tr>
          <template v-for="d in despesas" :key="d.plano.id">
            <tr>
              <td colspan="2" class="fw-bold">{{ d.plano.nome }}</td>
            </tr>
            <tr v-for="item in d.itens" :key="item.subconta">
              <td class="ps-4">{{ item.subconta }}</td>
              <td class="text-right">{{ formatarMoeda(item.valor) }}</td>
            </tr>
          </template>
          <tr>
            <td><strong>Total Geral</strong></td>
            <td class="text-right"><strong>{{ formatarMoeda(totalDespesas) }}</strong></td>
          </tr>
        </tbody>
      </table>
      <!-- RESULTADO ECONÔMICO -->
      <div class="mb-4">
        <h6>Resultado Econômico</h6>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Item</th>
              <th class="text-right">{{ subtitle }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Resultado</strong></td>
              <td class="text-right"><strong>{{ formatarMoeda(resultado) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- RENTABILIDADE -->
      <div>
        <h6>Rentabilidade</h6>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Item</th>
              <th class="text-right">%</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Rentabilidade</strong></td>
              <td class="text-right"><strong>{{ rentabilidade.toFixed(2) }}%</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RelatorioPdr'
}
</script>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'

const props = defineProps({
  printMode:  { type: Boolean, default: false },
  dataInicio: String,
  dataFim:    String,
  printTitle: String,
  subtitle:   String
})

const router = useRouter()
const meses = ['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ']
const mes  = ref(new Date().getMonth()+1)
const ano  = ref(new Date().getFullYear())

// estados do relatório
const receitas        = ref([])
const totalReceitas   = ref(0)
const despesas        = ref([])
const totalDespesas   = ref(0)
const resultado       = ref(0)
const rentabilidade   = ref(0)

onMounted(async () => {
  if (!props.printMode) return
  const res = await axios.get('/gestao/relatorios/financeiros/pdr', {
    params: { dataInicio: props.dataInicio, dataFim: props.dataFim }
  })
  receitas.value       = res.data.receitas
  totalReceitas.value  = parseFloat(res.data.totalReceitas)
  despesas.value       = res.data.despesas
  totalDespesas.value  = parseFloat(res.data.totalDespesas)
  resultado.value      = parseFloat(res.data.resultado)
  rentabilidade.value  = parseFloat(res.data.rentabilidade)
})

function abrirPrint() {
  const mm     = String(mes.value).padStart(2,'0')
  const inicio = `${ano.value}-${mm}-01`
  const fim    = new Date(ano.value, mes.value, 0)
  const fimDay = String(fim.getDate()).padStart(2,'0')
  const fimStr = `${ano.value}-${mm}-${fimDay}`

  router.push({
    path: `/gestao/relatorios/print/pdr`,
    query: {
      title:      'PDR Mensal',
      dataInicio: inicio,
      dataFim:    fimStr,
      subtitle:   `${meses[mes.value-1]}/${ano.value}`
    }
  })
}

function formatarMoeda(v) {
  return Number(v).toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL'
  })
}
</script>

<style scoped>
.text-right { text-align: right !important; }
.mb-4       { margin-bottom: 1.5rem; }
.ps-4       { padding-left: 1.5rem !important; }
.fw-bold    { font-weight: bold; }
</style>
